const OPENPAY_JS = 'https://js.openpay.mx/openpay.v1.min.js';
const OPENPAY_DATA_JS = 'https://js.openpay.mx/openpay-data.v1.min.js';

export interface OpenPayToken {
  id: string;
  card?: {
    brand?: string;
    card_number?: string;
  };
}

export interface OpenPayError {
  message?: string;
  description?: string;
  error_code?: number;
  category?: string;
}

export interface OpenPayCardParams {
  card_number: string;
  holder_name: string;
  expiration_year: string;
  expiration_month: string;
  cvv2: string;
}

export interface OpenPayStatic {
  setId: (id: string) => void;
  setApiKey: (key: string) => void;
  setSandboxMode: (flag: boolean) => void;
  token: {
    create: (
      params: { card: OpenPayCardParams },
      success: (response: { data: OpenPayToken }) => void,
      error: (response: { data: OpenPayError; message?: string }) => void,
    ) => void;
  };
  deviceData: {
    setup: (formId?: string, hiddenFieldName?: string) => string;
  };
  card: {
    validateCardNumber: (n: string) => boolean;
    validateCVC: (cvc: string, number?: string) => boolean;
    validateExpiry: (month: string, year: string) => boolean;
    cardType: (n: string) => string;
  };
}

declare global {
  interface Window {
    OpenPay?: OpenPayStatic;
  }
}

function loadScript(src: string): Promise<void> {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return existing.getAttribute('data-loaded') === '1'
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('No se pudo cargar OpenPay.js')));
        });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.setAttribute('data-loaded', '1');
      resolve();
    };
    script.onerror = () => reject(new Error('No se pudo cargar OpenPay.js'));
    document.head.appendChild(script);
  });
}

export async function loadOpenPay(
  merchantId: string,
  publicKey: string,
  sandbox: boolean,
): Promise<OpenPayStatic> {
  await loadScript(OPENPAY_JS);
  await loadScript(OPENPAY_DATA_JS);

  const OpenPay = window.OpenPay;
  if (!OpenPay) {
    throw new Error('OpenPay.js no está disponible');
  }

  OpenPay.setId(merchantId);
  OpenPay.setApiKey(publicKey);
  OpenPay.setSandboxMode(sandbox);
  return OpenPay;
}

export function setupDeviceSession(OpenPay: OpenPayStatic): string {
  const id = OpenPay.deviceData.setup();
  if (!id) {
    throw new Error('No se pudo generar el identificador antifraude de OpenPay');
  }
  return id;
}

export function tokenizeCard(
  OpenPay: OpenPayStatic,
  card: OpenPayCardParams,
): Promise<string> {
  return new Promise((resolve, reject) => {
    OpenPay.token.create(
      { card },
      (response) => {
        const id = response?.data?.id;
        if (!id) {
          reject(new Error('OpenPay no devolvió el token de la tarjeta'));
          return;
        }
        resolve(id);
      },
      (response) => {
        const msg =
          response?.data?.description ||
          response?.data?.message ||
          response?.message ||
          'No se pudo tokenizar la tarjeta';
        reject(new Error(msg));
      },
    );
  });
}

export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

export function formatCardNumber(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

export function browserHttpContext(): Record<string, string | boolean> {
  return {
    browser: navigator.userAgent,
    domain: window.location.hostname,
    browser_screen_height: String(window.screen.height),
    browser_screen_width: String(window.screen.width),
    browser_user_agent: navigator.userAgent,
    browser_language: navigator.language || 'es-MX',
    browser_java_enabled: false,
    browser_javascript_enabled: true,
  };
}
